import { Component } from '@theme/component';

/**
 * @typedef {Object} EmailSignupRefs
 * @property {HTMLFormElement} form - The customer form
 * @property {HTMLInputElement} emailInput - The email input
 * @property {HTMLButtonElement} submitButton - The submit button
 * @property {HTMLElement} status - Live region that holds the success / error message
 * @property {HTMLTemplateElement} successTemplate - Success message markup rendered by Liquid
 */

/**
 * Submits the newsletter form in the background and renders Shopify's
 * response message in place, so the page no longer reloads and jumps to
 * the top after signing up.
 *
 * The customer form has no JSON endpoint: Shopify redirects back to the
 * referring page with the message rendered into this block. We fetch that
 * page and lift the message out of it. When no message can be found (for
 * example when Shopify answers with a bot challenge) we fall back to a
 * regular form submission so the native flow still works.
 *
 * @extends {Component<EmailSignupRefs>}
 */
class EmailSignupComponent extends Component {
  requiredRefs = ['form', 'emailInput', 'submitButton', 'status', 'successTemplate'];

  #submitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.#revealServerMessage();
  }

  /**
   * Handles the form submission.
   *
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.#submitting) return;

    this.#setBusy(true);

    try {
      const response = await fetch(this.refs.form.action, {
        method: 'POST',
        body: new FormData(this.refs.form),
        headers: { Accept: 'text/html' },
      });
      const message = extractMessage(await response.text(), this.refs.status.id) ?? this.#successMessage(response);

      if (!message) {
        this.#submitNatively();
        return;
      }

      this.#renderMessage(message);
    } catch (error) {
      console.error('Email signup failed:', error);
      this.#submitNatively();
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Shopify confirms a signup by redirecting to `return_to` with
   * `customer_posted=true`. That page may not render this block's message
   * (for example when `return_to` points elsewhere), so use the local
   * success template whenever the redirect itself signals success.
   *
   * @param {Response} response
   * @returns {HTMLElement | null}
   */
  #successMessage(response) {
    if (!response.ok || !response.redirected) return null;

    const posted = new URL(response.url).searchParams.get('customer_posted') === 'true';
    const template = this.refs.successTemplate.content.firstElementChild;

    return posted && template instanceof HTMLElement ? template : null;
  }

  /**
   * Places the message from the server response into the live region and
   * updates the input's accessibility state to match.
   *
   * @param {HTMLElement} message
   */
  #renderMessage(message) {
    const { emailInput, status } = this.refs;
    const isError = message.dataset.type === 'error';

    status.replaceChildren(document.importNode(message, true));
    emailInput.setAttribute('aria-describedby', message.id);

    if (isError) {
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.focus();
      return;
    }

    emailInput.removeAttribute('aria-invalid');
    emailInput.value = '';
    this.#focusMessage();
  }

  /**
   * After a regular (non-JS) submission Shopify reloads the page with the
   * message rendered by Liquid. Bring it into view so the result is visible
   * regardless of where the browser restored the scroll position.
   */
  #revealServerMessage() {
    if (!this.refs.status.querySelector('.email-signup__message')) return;

    this.#focusMessage();
  }

  #focusMessage() {
    const message = this.refs.status.querySelector('.email-signup__message');

    if (!(message instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      message.scrollIntoView({ block: 'center' });
      message.focus({ preventScroll: true });
    });
  }

  /**
   * Submits the form without JavaScript interception.
   */
  #submitNatively() {
    this.refs.form.submit();
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#submitting = busy;
    this.refs.form.setAttribute('aria-busy', String(busy));
    this.refs.submitButton.disabled = busy;
  }
}

/**
 * Finds this block's message inside the HTML page Shopify redirected to.
 *
 * @param {string} html - The full page returned after the redirect
 * @param {string} statusId - The id of this block's status region
 * @returns {HTMLElement | null}
 */
const extractMessage = (html, statusId) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const message = doc.getElementById(statusId)?.querySelector('.email-signup__message');

  return message instanceof HTMLElement ? message : null;
};

if (!customElements.get('email-signup-component')) {
  customElements.define('email-signup-component', EmailSignupComponent);
}
